import React, { useState, useEffect } from 'react';
import { AlertIcon } from '../../../components/common/Icons';
import { VersionResponse, VersionCreatePayload, VersionUpdatePayload } from '../types';

interface VersionFormModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  version: VersionResponse | null;
  onClose: () => void;
  onSubmit: (payload: VersionCreatePayload | VersionUpdatePayload) => Promise<void>;
  saving: boolean;
  error: string | null;
  onClearError: () => void;
}

const fieldStyle: React.CSSProperties = { marginBottom: '16px' };
const labelStyle: React.CSSProperties = { display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: 600 };
const inputStyle: React.CSSProperties = { width: '100%' };
const textareaStyle: React.CSSProperties = { width: '100%', minHeight: '60px', resize: 'vertical' };
const sectionStyle: React.CSSProperties = { borderTop: '1px solid rgba(255,255,255,0.1)', paddingTop: '16px', marginTop: '16px' };

const VersionFormModal: React.FC<VersionFormModalProps> = ({
  isOpen, mode, version, onClose, onSubmit, saving, error, onClearError,
}) => {
  const [versionStr, setVersionStr] = useState('');
  const [codename, setCodename] = useState('');
  const [releasedAt, setReleasedAt] = useState('');
  const [apiImage, setApiImage] = useState('');
  const [uiImage, setUiImage] = useState('');
  const [minUpgradeFrom, setMinUpgradeFrom] = useState('');
  const [changelog, setChangelog] = useState('');
  const [breakingChanges, setBreakingChanges] = useState('');
  const [newFeatures, setNewFeatures] = useState('');
  const [isStable, setIsStable] = useState(true);
  const [isLts, setIsLts] = useState(false);
  const [isSecurityPatch, setIsSecurityPatch] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);

  // Populate form when editing
  useEffect(() => {
    if (mode === 'edit' && version) {
      setVersionStr(version.version);
      setCodename(version.codename || '');
      setReleasedAt(version.released_at ? version.released_at.split('T')[0] : '');
      setApiImage(version.api_image);
      setUiImage(version.ui_image);
      setMinUpgradeFrom(version.minimum_upgrade_from || '');
      setChangelog(version.changelog?.join('\n') || '');
      setBreakingChanges(version.breaking_changes?.join('\n') || '');
      setNewFeatures(version.new_features?.join('\n') || '');
      setIsStable(version.is_stable);
      setIsLts(version.is_lts);
      setIsSecurityPatch(version.is_security_patch);
    } else if (mode === 'create') {
      setVersionStr('');
      setCodename('');
      setReleasedAt('');
      setApiImage('');
      setUiImage('');
      setMinUpgradeFrom('');
      setChangelog('');
      setBreakingChanges('');
      setNewFeatures('');
      setIsStable(true);
      setIsLts(false);
      setIsSecurityPatch(false);
    }
    setValidationError(null);
  }, [mode, version, isOpen]);

  if (!isOpen) return null;

  const splitLines = (text: string): string[] | undefined => {
    const lines = text.split('\n').map(l => l.trim()).filter(Boolean);
    return lines.length > 0 ? lines : undefined;
  };

  const handleSubmit = async () => {
    // Validate required fields
    if (mode === 'create') {
      if (!versionStr.trim()) { setValidationError('Version is required'); return; }
      if (!apiImage.trim()) { setValidationError('API image tag is required'); return; }
      if (!uiImage.trim()) { setValidationError('UI image tag is required'); return; }

      const payload: VersionCreatePayload = {
        version: versionStr.trim(),
        api_image: apiImage.trim(),
        ui_image: uiImage.trim(),
        codename: codename.trim() || undefined,
        released_at: releasedAt || undefined,
        minimum_upgrade_from: minUpgradeFrom.trim() || undefined,
        changelog: splitLines(changelog),
        breaking_changes: splitLines(breakingChanges),
        new_features: splitLines(newFeatures),
        is_stable: isStable,
        is_lts: isLts,
        is_security_patch: isSecurityPatch,
      };
      await onSubmit(payload);
    } else {
      const payload: VersionUpdatePayload = {
        codename: codename.trim() || undefined,
        api_image: apiImage.trim() || undefined,
        ui_image: uiImage.trim() || undefined,
        released_at: releasedAt || undefined,
        minimum_upgrade_from: minUpgradeFrom.trim() || undefined,
        changelog: splitLines(changelog),
        breaking_changes: splitLines(breakingChanges),
        new_features: splitLines(newFeatures),
        is_stable: isStable,
        is_lts: isLts,
        is_security_patch: isSecurityPatch,
      };
      await onSubmit(payload);
    }
  };

  const title = mode === 'create' ? 'Register New Platform Version' : `Edit Version: ${version?.version}`;
  const submitLabel = mode === 'create'
    ? (saving ? 'Registering...' : 'Register Version')
    : (saving ? 'Saving...' : 'Save Changes');

  const displayError = validationError || error;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-lg" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '640px', maxHeight: '80vh', overflow: 'auto' }}>
        <div className="modal-header">
          <h2>{title}</h2>
          <button className="btn btn-ghost" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          {displayError && (
            <div className="alert alert-error" style={{ marginBottom: '16px', padding: '12px 16px', borderRadius: '8px', background: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.5)', color: 'var(--error)', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <AlertIcon size={18} />
              <span style={{ flex: 1 }}>{displayError}</span>
              <button className="btn btn-ghost btn-sm" onClick={() => { setValidationError(null); onClearError(); }} style={{ padding: '4px', minWidth: 'auto' }}>×</button>
            </div>
          )}

          {/* Version & Codename */}
          <div style={fieldStyle}>
            <label style={labelStyle}>Version *</label>
            <input className="input" style={inputStyle} value={versionStr} onChange={(e) => setVersionStr(e.target.value)} placeholder="e.g. 5.19.0" disabled={mode === 'edit'} />
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle}>Codename</label>
            <input className="input" style={inputStyle} value={codename} onChange={(e) => setCodename(e.target.value)} placeholder="e.g. Phoenix" />
          </div>
          <div style={fieldStyle}>
            <label style={labelStyle}>Release Date</label>
            <input className="input" style={inputStyle} type="date" value={releasedAt} onChange={(e) => setReleasedAt(e.target.value)} />
          </div>

          {/* Image Tags */}
          <div style={sectionStyle}>
            <h3 style={{ fontSize: '14px', marginBottom: '12px' }}>Image Tags</h3>
            <div style={fieldStyle}>
              <label style={labelStyle}>API Image *</label>
              <input className="input" style={inputStyle} value={apiImage} onChange={(e) => setApiImage(e.target.value)} placeholder="platform-api:5.19.0" />
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>UI Image *</label>
              <input className="input" style={inputStyle} value={uiImage} onChange={(e) => setUiImage(e.target.value)} placeholder="platform-ui:5.19.0" />
            </div>
          </div>

          {/* Upgrade Path */}
          <div style={sectionStyle}>
            <h3 style={{ fontSize: '14px', marginBottom: '12px' }}>Upgrade Path</h3>
            <div style={fieldStyle}>
              <label style={labelStyle}>Minimum Upgrade From</label>
              <input className="input" style={inputStyle} value={minUpgradeFrom} onChange={(e) => setMinUpgradeFrom(e.target.value)} placeholder="e.g. 5.16.0" />
            </div>
          </div>

          {/* Flags */}
          <div style={sectionStyle}>
            <h3 style={{ fontSize: '14px', marginBottom: '12px' }}>Flags</h3>
            <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                <input type="checkbox" checked={isStable} onChange={(e) => setIsStable(e.target.checked)} /> Stable
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                <input type="checkbox" checked={isLts} onChange={(e) => setIsLts(e.target.checked)} /> LTS
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', cursor: 'pointer' }}>
                <input type="checkbox" checked={isSecurityPatch} onChange={(e) => setIsSecurityPatch(e.target.checked)} /> Security Patch
              </label>
            </div>
          </div>

          {/* Release Notes */}
          <div style={sectionStyle}>
            <h3 style={{ fontSize: '14px', marginBottom: '12px' }}>Release Notes</h3>
            <div style={fieldStyle}>
              <label style={labelStyle}>Changelog (one item per line)</label>
              <textarea className="input" style={textareaStyle} value={changelog} onChange={(e) => setChangelog(e.target.value)} placeholder="List changes, one per line" />
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>Breaking Changes (one item per line)</label>
              <textarea className="input" style={textareaStyle} value={breakingChanges} onChange={(e) => setBreakingChanges(e.target.value)} placeholder="List breaking changes, one per line" />
            </div>
            <div style={fieldStyle}>
              <label style={labelStyle}>New Features (one item per line)</label>
              <textarea className="input" style={textareaStyle} value={newFeatures} onChange={(e) => setNewFeatures(e.target.value)} placeholder="List new features, one per line" />
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={saving}>{submitLabel}</button>
        </div>
      </div>
    </div>
  );
};

export default VersionFormModal;
